import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@contexts/ThemeContext';
import { PermissionProvider } from '@contexts/PermissionContext';
import Login from '@pages/auth/Login';
import ResetPassword from '@pages/auth/ResetPassword';
import RoleSelection from '@pages/auth/RoleSelection';
import Unauthorized from '@pages/auth/Unauthorized';
import DashboardRouter from '@components/DashboardRouter/DashboardRouter';
import DailyGate from '@components/DailyGate/DailyGate';
import ErrorBoundary from '@components/common/ErrorBoundary';
import UpdateAvailableBanner from '@components/common/UpdateAvailableBanner';
import DailyLogRouter from '@components/DailyLogRouter/DailyLogRouter';
import HolidayRouter from '@components/HolidayRouter/HolidayRouter';
import AttendanceHistoryRouter from '@components/AttendanceHistoryRouter/AttendanceHistoryRouter';
import HolidayCalendarViewRouter from '@components/HolidayCalendarViewRouter/HolidayCalendarViewRouter';
import ProtectedRoute from '@components/ProtectedRoute/ProtectedRoute';
import UserManagement from '@pages/admin/UserManagement';
import ManageUsers from '@pages/admin/ManageUsers';
import ManageOrganizationalAdmins from '@pages/admin/ManageOrganizationalAdmins';
import CreateOrgAdmin from '@pages/admin/CreateOrgAdmin';
import ViewOrgAdmin from '@pages/admin/ViewOrgAdmin';
import AdminDashboard from '@pages/admin/AdminDashboard';
import UserProfileSettings from '@pages/profile/UserProfileSettings';
import EnhancedProgramList from '@modules/programManager/components/EnhancedProgramList';
import ChecklistPage from '@modules/programManager/components/ChecklistPage';
import ProgramProgressView from '@modules/programManager/components/ProgramProgressView';
import ChecklistManagement from '@modules/programManager/components/ChecklistManagement';
import TaskManagement from '@modules/programManager/components/TaskManagement';
import WorkshopsPage from '@pages/workshops/WorkshopsPage';
import WorkshopTasksPage from '@pages/workshops/WorkshopTasksPage';
import NotificationsPage from '@pages/admin/NotificationsPage';
import ReportsPage from '@pages/admin/ReportsPage';
import RecentActivities from '@pages/admin/RecentActivities';
import EmployeesPage from '@pages/hr/EmployeesPage';
import EmployeeEditPage from '@pages/hr/EmployeeEditPage';
import EmployeeProfilePage from '@pages/hr/EmployeeProfilePage';
import DailyTrackerPage from '@pages/hr/DailyTrackerPage';
import PlanBoardPage from '@pages/hr/planReports/PlanBoardPage';
import PeriodReportPage from '@pages/hr/planReports/PeriodReportPage';
import SlippagePage from '@pages/hr/planReports/SlippagePage';
import AnalyticsPage from '@pages/hr/planReports/AnalyticsPage';
import DailyUpdatesFeedPage from '@pages/hr/DailyUpdatesFeedPage';
import LeavesPage from '@pages/hr/LeavesPage';
import HRProfileSettings from '@pages/hr/HRProfileSettings';
import HRDailyLog from '@pages/hr/HRDailyLog';
import HRAttendanceHistory from '@pages/hr/HRAttendanceHistory';
import JobPostingsPage from '@pages/hr/JobPostingsPage';
import JobPostingFormPage from '@pages/hr/JobPostingFormPage';
import RecognitionsPage from '@pages/hr/RecognitionsPage';
import JobApplicationsPage from '@pages/hr/JobApplicationsPage';
import WallOfFamePage from '@pages/common/WallOfFamePage';
import MyWork from '@pages/employee/MyWork';
import LeavePortal from '@pages/employee/LeavePortal';
import AttendanceHistory from '@pages/employee/AttendanceHistory';
import TeamAttendance from '@pages/employee/TeamAttendance';
import HolidayList from '@pages/employee/HolidayList';
import HolidayCalendarView from '@pages/employee/HolidayCalendarView';
import HRHolidayList from '@pages/hr/HRHolidayList';
import HRHolidayCalendarView from '@pages/hr/HRHolidayCalendarView';
import HolidayManagement from '@pages/hr/HolidayManagement';
import LeavePolicySettings from '@pages/hr/LeavePolicySettings';
import PMHolidayList from '@pages/programManager/HolidayList';
import PMHolidayCalendarView from '@pages/programManager/HolidayCalendarView';
import AdminHolidayList from '@pages/admin/AdminHolidayList';
import AdminHolidayCalendarView from '@pages/admin/AdminHolidayCalendarView';
import PMDailyLog from '@pages/programManager/DailyLog';
import PMAttendanceHistory from '@pages/programManager/PMAttendanceHistory';
import AdminDailyLog from '@pages/admin/DailyLog';
import AdminAttendanceHistory from '@pages/admin/AdminAttendanceHistory';
import SuperAdminDashboard from '@pages/admin/SuperAdminDashboard';
import { Layout } from '@layout/Layout';
import { useAuth } from '@store/authStore';
import { initializeStores } from '@store/index';
import { initializeVersionManagement } from '@services/VersionService';
import PayRoll from './pages/hr/PayrollManagement/PayRoll';
import PayRollDetails from './pages/hr/PayrollManagement/PayRollDetails';
import AddEmployeePayroll from './pages/hr/PayrollManagement/AddEmployeePayroll';
import EditEmployeePayroll from './pages/hr/PayrollManagement/EditEmployeePayroll';
import ViewEmployeeSalarySlip from './pages/hr/PayrollManagement/ViewEmployeeSalarySlip';
import MonthlySalaryReview from '@pages/hr/MonthlySalaryReview';
import AssessmentDashboard from '@pages/assessmentCreator/AssessmentDashboard';
import ManageAssessments from '@pages/assessmentCreator/ManageAssessments';
import AssessmentResults from '@pages/assessmentCreator/AssessmentResults';
import CreateAssessment from '@pages/assessmentCreator/CreateAssessment';
import ModuleAssessmentMapping from '@pages/assessmentCreator/ModuleAssessmentMapping';
import CreateQuestion from '@pages/assessmentCreator/CreateQuestion';
import Users from '@pages/assessmentCreator/Users';
import ManageDimensionsModules from '@pages/assessmentCreator/ManageDimensionsModules';
import ManageAssessmentEligibility from '@pages/assessmentCreator/ManageAssessmentEligibility';
import ManageAssessmentDomains from '@pages/assessmentCreator/ManageAssessmentDomains';
import ReportingManagerManagement from '@pages/assessmentCreator/feedback360/ReportingManagerManagement';
import ReporteeManagement from '@pages/assessmentCreator/feedback360/ReporteeManagement';
import ConsolidatedResultSelection from '@pages/assessmentCreator/ConsolidatedResultSelection';
import OrganizationFeedPage from '@pages/OrganizationFeed/OrganizationFeedPage';
import OrganizationSetupPage from '@pages/OrganizationSetup/OrganizationSetupPage';
import AssignLeaderPage from '@pages/OrganizationSetup/AssignLeaderPage';
import PublicJobsPage from '@pages/public/PublicJobsPage';
import PublicJobDetailPage from '@pages/public/PublicJobDetailPage';
import VerifyPayslip from '@pages/public/VerifyPayslip';
import ViewPublicSalarySlip from '@pages/public/ViewPublicSalarySlip';
import OKRDashboard from '@modules/okr/pages/OKRDashboard';
import MyOKRs from '@modules/okr/pages/MyOKRs';
import TeamOKRs from '@modules/okr/pages/TeamOKRs';
import MagnetixContactSubmissions from '@pages/products/MagnetixContactSubmissions';

// Debug component to track route changes
function RouteDebugger() {
  const location = useLocation();

  useEffect(() => {
    console.log('App: Route changed to:', location.pathname);
  }, [location.pathname]);

  return null;
}

function App() {
  const { initializeAuth, isLoading, isAuthenticated, user } = useAuth();
  const [authInitialized, setAuthInitialized] = React.useState(false);

  useEffect(() => {
    // Initialize the store registry for centralized reset functionality
    initializeStores();

    // Clear mock data if present
    const token = localStorage.getItem('authToken');
    if (token && token.includes('mock-token')) {
      console.log('🧹 App: Detected mock data, clearing...');
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      return;
    }

    // Initialize authentication state from localStorage when app loads
    console.log('App: Initializing auth...');
    initializeAuth();
    setAuthInitialized(true);

    // Initialize version management for automatic update checks
    initializeVersionManagement();
  }, [initializeAuth]);

  // Debug: Log authentication state changes
  useEffect(() => {
    console.log('App: Authentication state changed:', { isAuthenticated, user: !!user, isLoading });
    console.log('App: localStorage check:', {
      token: localStorage.getItem('authToken'),
      user: localStorage.getItem('user')
    });
  }, [isAuthenticated, user, isLoading]);

  // Show loading while initializing
  if (isLoading || !authInitialized) {
    console.log('App: Still loading...', { isLoading, authInitialized });
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '18px',
        backgroundColor: '#0a0a0a',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid rgba(33, 150, 243, 0.3)',
            borderTop: '3px solid #2196F3',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <div>Initializing...</div>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  console.log('App: Auth state after loading:', { isAuthenticated, user: !!user });

  return (
    <ThemeProvider>
      <PermissionProvider>
        <Router>
          <RouteDebugger />
          {/* "A new version is available" — sits above every route AND above the
              blocking daily gate, which would otherwise cover it. */}
          <UpdateAvailableBanner />
          {/* App-wide mandatory daily plan / EOD gate (indirect punch-in).
              Boundaried so a render error in the gate can never white-screen the app. */}
          <ErrorBoundary title="Daily check-in unavailable">
            <DailyGate />
          </ErrorBoundary>
        <Routes>
          {/* Public Routes - No Authentication Required */}
          <Route path="/careers" element={<PublicJobsPage />} />
          <Route path="/careers/:id" element={<PublicJobDetailPage />} />
          <Route path="/verify-payslip/:barcodeData" element={<VerifyPayslip />} />
          <Route path="/verify-payslip" element={<VerifyPayslip />} />
          <Route path="/salary-slip/:id/:token" element={<ViewPublicSalarySlip />} />

          <Route path="/login" element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />
          } />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/role-selection" element={
            <ProtectedRoute>
              <RoleSelection />
            </ProtectedRoute>
          } />
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <DashboardRouter />
            </ProtectedRoute>
          } />

          {/* Admin Routes */}
          <Route path="/role-decider" element={
            <ProtectedRoute>
              <Layout>
                <SuperAdminDashboard />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/manage-users" element={
            <ProtectedRoute>
              <ManageUsers />
            </ProtectedRoute>
          } />
          <Route path="/manage-organizational-admins" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <ManageOrganizationalAdmins />
            </ProtectedRoute>
          } />
          <Route path="/manage-organizational-admins/create" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <CreateOrgAdmin />
            </ProtectedRoute>
          } />
          <Route path="/manage-organizational-admins/edit/:id" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <CreateOrgAdmin />
            </ProtectedRoute>
          } />
          <Route path="/manage-organizational-admins/view/:id" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <ViewOrgAdmin />
            </ProtectedRoute>
          } />
          <Route path="/daily-log" element={
            <ProtectedRoute>
              <DailyLogRouter />
            </ProtectedRoute>
          } />
          <Route path="/attendance-history" element={
            <ProtectedRoute>
              <AttendanceHistoryRouter />
            </ProtectedRoute>
          } />
          <Route path="/daily-updates" element={
            <ProtectedRoute>
              <DailyUpdatesFeedPage />
            </ProtectedRoute>
          } />
          {/* Legacy admin routes - redirect to new paths */}
          <Route path="/admin/dashboard" element={
            <ProtectedRoute>
              <Navigate to="/dashboard" replace />
            </ProtectedRoute>
          } />
          <Route path="/admin/manage-users" element={
            <ProtectedRoute>
              <Navigate to="/manage-users" replace />
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute>
              <Navigate to="/dashboard" replace />
            </ProtectedRoute>
          } />

          {/* Program Manager Routes */}
          <Route path="/users" element={
            <ProtectedRoute>
              <UserManagement />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute>
              <Navigate to="/users" replace />
            </ProtectedRoute>
          } />

          {/* Deleted routes: ViewAllTasks, AllProjectsPage, ProjectsPage */}
          <Route path="/admin/tasks" element={
            <ProtectedRoute>
              <Navigate to="/sessions" replace />
            </ProtectedRoute>
          } />
          <Route path="/admin/view-all-tasks" element={
            <ProtectedRoute>
              <Navigate to="/sessions" replace />
            </ProtectedRoute>
          } />
          <Route path="/programs" element={
            <ProtectedRoute>
              <Navigate to="/sessions" replace />
            </ProtectedRoute>
          } />
          <Route path="/admin/projects" element={
            <ProtectedRoute>
              <Navigate to="/sessions" replace />
            </ProtectedRoute>
          } />
          <Route path="/admin/all-projects" element={
            <ProtectedRoute>
              <Navigate to="/sessions" replace />
            </ProtectedRoute>
          } />

          <Route path="/notifications" element={
            <ProtectedRoute>
              <NotificationsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/notifications" element={
            <ProtectedRoute>
              <Navigate to="/notifications" replace />
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute>
              <ReportsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/reports" element={
            <ProtectedRoute>
              <Navigate to="/reports" replace />
            </ProtectedRoute>
          } />
          <Route path="/recent-activities" element={
            <ProtectedRoute>
              <Layout>
                <RecentActivities />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/profile/settings" element={
            <ProtectedRoute>
              <UserProfileSettings />
            </ProtectedRoute>
          } />

          {/* Organization Feed - accessible by all authenticated users */}
          <Route path="/organization-feed" element={
            <ProtectedRoute>
              <OrganizationFeedPage />
            </ProtectedRoute>
          } />

          {/* Organization Setup - accessible by all authenticated users, edit only for HR/Admin */}
          <Route path="/organization" element={
            <ProtectedRoute>
              <Layout>
                <OrganizationSetupPage />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Assign Leader - HR and Admin only */}
          <Route path="/organization/assign-leader" element={
            <ProtectedRoute>
              <Layout>
                <AssignLeaderPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/organization/assign-leader/:id" element={
            <ProtectedRoute>
              <Layout>
                <AssignLeaderPage />
              </Layout>
            </ProtectedRoute>
          } />

          {/* OKR Routes - accessible by all authenticated users */}
          <Route path="/okr" element={
            <ProtectedRoute>
              <OKRDashboard />
            </ProtectedRoute>
          } />
          <Route path="/okr/my" element={
            <ProtectedRoute>
              <MyOKRs />
            </ProtectedRoute>
          } />
          <Route path="/okr/team" element={
            <ProtectedRoute>
              <TeamOKRs />
            </ProtectedRoute>
          } />

          {/* HR Routes */}
          <Route path="/employees/:id/profile" element={
            <ProtectedRoute>
              <EmployeeProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/employees/:id/edit" element={
            <ProtectedRoute>
              <EmployeeEditPage />
            </ProtectedRoute>
          } />
          <Route path="/employees" element={
            <ProtectedRoute>
              <EmployeesPage />
            </ProtectedRoute>
          } />
          <Route path="/daily-tracker" element={
            <ProtectedRoute>
              <DailyTrackerPage />
            </ProtectedRoute>
          } />
          {/* Daily Plans & Reports. Permission comes from config/routes.ts, which
              also lets Employee-role team leads through — the API scopes them to
              their own reportees. */}
          <Route path="/hr/plan-reports/board" element={
            <ProtectedRoute>
              <PlanBoardPage />
            </ProtectedRoute>
          } />
          <Route path="/hr/plan-reports/period" element={
            <ProtectedRoute>
              <PeriodReportPage />
            </ProtectedRoute>
          } />
          <Route path="/hr/plan-reports/slippage" element={
            <ProtectedRoute>
              <SlippagePage />
            </ProtectedRoute>
          } />
          <Route path="/hr/plan-reports/analytics" element={
            <ProtectedRoute>
              <AnalyticsPage />
            </ProtectedRoute>
          } />
          <Route path="/daily-updates-feed" element={
            <ProtectedRoute>
              <DailyUpdatesFeedPage />
            </ProtectedRoute>
          } />
          <Route path="/leaves" element={
            <ProtectedRoute>
              <LeavesPage />
            </ProtectedRoute>
          } />
          <Route path="/hr/jobs" element={
            <ProtectedRoute>
              <JobPostingsPage />
            </ProtectedRoute>
          } />
          <Route path="/hr/jobs/new" element={
            <ProtectedRoute>
              <JobPostingFormPage />
            </ProtectedRoute>
          } />
          <Route path="/hr/jobs/:id/edit" element={
            <ProtectedRoute>
              <JobPostingFormPage />
            </ProtectedRoute>
          } />
          <Route path="/hr/recognitions" element={
            <ProtectedRoute>
              <RecognitionsPage />
            </ProtectedRoute>
          } />
          <Route path="/wall-of-fame" element={
            <ProtectedRoute>
              <WallOfFamePage />
            </ProtectedRoute>
          } />
          <Route path="/hr/applications" element={
            <ProtectedRoute>
              <JobApplicationsPage />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <HRProfileSettings />
            </ProtectedRoute>
          } />
          <Route path="/holidays" element={
            <ProtectedRoute>
              <HolidayRouter />
            </ProtectedRoute>
          } />
          <Route path="/holiday-calendar-view" element={
            <ProtectedRoute>
              <HolidayCalendarViewRouter />
            </ProtectedRoute>
          } />
          <Route path="/holiday-management" element={
            <ProtectedRoute>
              <HolidayManagement />
            </ProtectedRoute>
          } />
          <Route path="/leave-policy-settings" element={
            <ProtectedRoute allowedRoles={['HR', 'Admin']}>
              <LeavePolicySettings />
            </ProtectedRoute>
          } />
           <Route path="/payroll" element={
            <ProtectedRoute>
              <PayRoll />
            </ProtectedRoute>
          } />
          <Route path="/hr/salary-review" element={
            <ProtectedRoute allowedRoles={['HR', 'Admin']}>
              <MonthlySalaryReview />
            </ProtectedRoute>
          } />
          <Route path="/payroll/:id" element={
            <ProtectedRoute>
              <PayRollDetails />
            </ProtectedRoute>
          } />
          <Route path="/payroll/:payrollId/add-employee" element={
            <ProtectedRoute>
              <AddEmployeePayroll />
            </ProtectedRoute>
          } />
          <Route path="/payroll/:payrollId/employee/:employeePayrollId/edit" element={
            <ProtectedRoute>
              <EditEmployeePayroll />
            </ProtectedRoute>
          } />
          <Route path="/payroll/:payrollId/employee/:employeePayrollId/view" element={
            <ProtectedRoute>
              <ViewEmployeeSalarySlip />
            </ProtectedRoute>
          } />
          {/* Legacy route - redirect to new path */}
          <Route path="/hr/payroll/:id" element={
            <ProtectedRoute>
              <Navigate to="/payroll/:id" replace />
            </ProtectedRoute>
          } />

          {/* Employee Routes */}
          <Route path="/my-work" element={
            <ProtectedRoute>
              <MyWork />
            </ProtectedRoute>
          } />
          <Route path="/team-attendance" element={
            <ProtectedRoute>
              <TeamAttendance />
            </ProtectedRoute>
          } />
          <Route path="/leave-portal" element={
            <ProtectedRoute>
              <LeavePortal />
            </ProtectedRoute>
          } />

          {/* Program Manager Routes */}

          {/* Management Index Routes */}
          <Route path="/management-index" element={
            <ProtectedRoute>
              <Navigate to="/sessions" replace />
            </ProtectedRoute>
          } />
          <Route path="/sessions" element={
            <ProtectedRoute>
              <Layout>
                <EnhancedProgramList />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/program-manager" element={
            <ProtectedRoute>
              <Navigate to="/sessions" replace />
            </ProtectedRoute>
          } />
          <Route path="/management-index/sessions" element={
            <ProtectedRoute>
              <Navigate to="/sessions" replace />
            </ProtectedRoute>
          } />
          <Route path="/management-index/sessions/tasks/:programId" element={
            <ProtectedRoute>
              <Layout>
                <TaskManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/management-index/sessions/categories" element={
            <ProtectedRoute>
              <Layout>
                <ChecklistManagement />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/management-index/sessions/checklist/:id" element={
            <ProtectedRoute>
              <Layout>
                <ChecklistPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/management-index/sessions/progress/:programId" element={
            <ProtectedRoute>
              <Layout>
                <ProgramProgressView />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/workshops" element={
            <ProtectedRoute>
              <Layout>
                <WorkshopsPage />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/management-index/workshops" element={
            <ProtectedRoute>
              <Navigate to="/workshops" replace />
            </ProtectedRoute>
          } />
          <Route path="/management-index/workshops/:programType" element={
            <ProtectedRoute>
              <Layout>
                <WorkshopTasksPage />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Legacy Program Manager Routes - Redirect to new structure */}
          <Route path="/program-manager" element={
            <ProtectedRoute>
              <Navigate to="/management-index/sessions" replace />
            </ProtectedRoute>
          } />
          <Route path="/program-manager/:programId/tasks" element={
            <ProtectedRoute>
              <Navigate to="/management-index/sessions/:programId/tasks" replace />
            </ProtectedRoute>
          } />
          <Route path="/program-manager/tasks/:programId?" element={
            <ProtectedRoute>
              <Navigate to="/management-index/sessions/tasks/:programId?" replace />
            </ProtectedRoute>
          } />
          <Route path="/program-manager/categories" element={
            <ProtectedRoute>
              <Navigate to="/management-index/sessions/categories" replace />
            </ProtectedRoute>
          } />
          <Route path="/program-manager/checklist/:id" element={
            <ProtectedRoute>
              <Navigate to="/management-index/sessions/checklist/:id" replace />
            </ProtectedRoute>
          } />
          <Route path="/program-manager/progress/:programId" element={
            <ProtectedRoute>
              <Navigate to="/management-index/sessions/progress/:programId" replace />
            </ProtectedRoute>
          } />

          {/* Assessment Creator Routes */}
          <Route path="/assessment-dashboard" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <AssessmentDashboard />
            </ProtectedRoute>
          } />
          <Route path="/manage-assessments" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <ManageAssessments />
            </ProtectedRoute>
          } />
          <Route path="/create-assessment" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <CreateAssessment />
            </ProtectedRoute>
          } />
          <Route path="/module-assessment-mapping" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <ModuleAssessmentMapping />
            </ProtectedRoute>
          } />
          <Route path="/create-question" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <CreateQuestion />
            </ProtectedRoute>
          } />
          <Route path="/assessment-results/:assessmentId" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <AssessmentResults />
            </ProtectedRoute>
          } />
          <Route path="/assessment-users" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <Users />
            </ProtectedRoute>
          } />
          <Route path="/manage-dimensions-modules" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <ManageDimensionsModules />
            </ProtectedRoute>
          } />
          <Route path="/manage-assessment-eligibility" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <ManageAssessmentEligibility />
            </ProtectedRoute>
          } />
          <Route path="/manage-assessment-domains" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <ManageAssessmentDomains />
            </ProtectedRoute>
          } />
          <Route path="/assessment-creator/360-feedback/reporting-managers" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <ReportingManagerManagement />
            </ProtectedRoute>
          } />
          <Route path="/assessment-creator/180-feedback/reportee-management" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <ReporteeManagement />
            </ProtectedRoute>
          } />
          <Route path="/assessment-results/:assessmentId/consolidated" element={
            <ProtectedRoute allowedRoles={['Assessment Creator']}>
              <ConsolidatedResultSelection />
            </ProtectedRoute>
          } />

          <Route path="/backend-test" element={
            <Layout>
              <div>Backend Test Page</div>
            </Layout>
          } />

          {/* Products Routes */}
          <Route path="/products/magnetix" element={
            <ProtectedRoute allowedRoles={['Admin', 'HR']}>
              <Layout>
                <MagnetixContactSubmissions />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Catch all route - only for unknown paths */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </Router>
      </PermissionProvider>
    </ThemeProvider>
  );
}

export default App;